import type { ContextualSnackbarData } from '@components/ui/feedback/Snackbar';
import type { AppointmentResponse, SeriesResponse } from '@store/generated/appointments';
import { RRule, rrulestr } from 'rrule';

import { formatDateTimeRange } from './dateTimeFormatters';

// Singleton timezone formatter for performance
// Creating Intl.DateTimeFormat is expensive - reuse same instance
const timezoneFormatter = new Intl.DateTimeFormat('en-US', {
  timeZoneName: 'short',
});

/**
 * Extract timezone abbreviation from a date (e.g., "ET", "PST")
 * Uses singleton formatter for better performance
 */
const getTimezoneAbbreviation = (date: Date): string => {
  return (
    timezoneFormatter.formatToParts(date).find(part => part.type === 'timeZoneName')?.value || ''
  );
};

/**
 * Parse rrule string to get frequency in human-readable format
 * Examples: "1x weekly", "2x monthly", "Daily"
 */
const getRecurrenceFrequency = (rruleString: string): string => {
  try {
    const rule = rrulestr(rruleString);
    if (!(rule instanceof RRule)) {
      return 'recurring';
    }

    const { freq, interval } = rule.options;

    // Map RRule frequency to readable string
    const freqMap: Record<number, string> = {
      [RRule.DAILY]: 'daily',
      [RRule.WEEKLY]: 'weekly',
      [RRule.MONTHLY]: 'monthly',
      [RRule.YEARLY]: 'yearly',
    };

    const freqName = freqMap[freq] || 'recurring';

    // If interval is 1, just return the frequency (e.g., "weekly")
    // Otherwise, return interval + frequency (e.g., "2x monthly")
    return interval === 1 ? freqName : `${interval}x ${freqName}`;
  } catch (error) {
    console.error('Error parsing rrule for notification:', error);
    return 'recurring';
  }
};

/**
 * Format appointment data for contextual snackbar notification
 * Works for both single appointments and series
 */
export const formatAppointmentCreatedNotification = (
  appointment: AppointmentResponse | SeriesResponse,
): ContextualSnackbarData => {
  // Determine if this is a SeriesResponse (has series_id and rrule at root, no appointment_id)
  // vs AppointmentResponse (has appointment_id and start_time)
  const isSeries = 'series_id' in appointment && !('appointment_id' in appointment);

  if (isSeries) {
    // For series, we have limited information
    const seriesAppointment = appointment;

    // Get recurrence info from rrule
    const recurrenceInfo = ` (${getRecurrenceFrequency(seriesAppointment.rrule)})`;

    // We don't have patient or start time for series, so show minimal info
    return {
      title: 'New Appointment Series Created!',
      details: [`Recurring appointment${recurrenceInfo}`],
    };
  }

  // For single appointments
  const singleAppointment = appointment;

  // Extract patient info
  const patientName = singleAppointment.patient?.name || 'Unknown Patient';

  // Get recurrence info if this appointment is part of a series
  let recurrenceInfo = '';
  if (singleAppointment.series?.rrule) {
    recurrenceInfo = ` (${getRecurrenceFrequency(singleAppointment.series.rrule)})`;
  }

  // Build first detail line: patient name + recurrence
  const patientLine = `${patientName}${recurrenceInfo}`;

  // Format date/time for second detail line
  const startTime = new Date(singleAppointment.start_time);
  const endTime = new Date(startTime.getTime() + singleAppointment.duration * 60000); // duration is in minutes

  // Get timezone abbreviation (e.g., "ET")
  const timezone = getTimezoneAbbreviation(startTime);

  const dateTimeLine = `${formatDateTimeRange(startTime, endTime)}${timezone ? ` ${timezone}` : ''}`;

  return {
    title: 'New Appointment Created!',
    details: [patientLine, dateTimeLine],
  };
};

/**
 * Format approved appointment data for contextual snackbar notification
 * Used when a suggested appointment is approved/accepted
 */
export const formatAppointmentApprovedNotification = (
  appointment: AppointmentResponse,
): ContextualSnackbarData => {
  // Extract patient info
  const patientName = appointment.patient?.name || 'Unknown Patient';

  // Format date/time for second detail line
  const startTime = new Date(appointment.start_time);
  const endTime = new Date(startTime.getTime() + appointment.duration * 60000); // duration is in minutes

  // Get timezone abbreviation (e.g., "ET")
  const timezone = getTimezoneAbbreviation(startTime);

  const dateTimeLine = `${formatDateTimeRange(startTime, endTime)}${timezone ? ` ${timezone}` : ''}`;

  return {
    title: 'Appointment Approved!',
    details: [patientName, dateTimeLine],
  };
};
